export const FIELD_CATEGORIES = {
  Toggle: {
    component: 'ToggleButton',
    label: 'Toggle',
    icon: 'toggle_on',
    hasSubTypes: false,
    configFields: ['options'],
  },
  Select: {
    component: 'Select',
    label: 'Select',
    icon: 'list',
    hasSubTypes: true,
    configFields: ['options', 'allow_multiple', 'allow_custom_input'],
  },
  Text: {
    component: 'TextField',
    label: 'Text',
    icon: 'text_fields',
    hasSubTypes: true,
    subTypes: ['TextField', 'LongText', 'RichText'],
    configFields: ['sub_type', 'prompt_text', 'default_text'],
  },
  Date: {
    component: 'KeyboardDatePicker',
    label: 'Date',
    icon: 'calendar_today',
    hasSubTypes: false,
    configFields: ['use_range', 'allow_previous_dates', 'default_date'],
  },
  Media: {
    component: 'DropArea',
    label: 'Media',
    icon: 'image',
    hasSubTypes: true,
    subTypes: ['DropZone', 'DropArea', 'Both'],
    configFields: ['sub_type', 'file_types_allowed', 'max_allowed', 'allow_inserts'],
  },
  Repeater: {
    component: 'JsonBuilder',
    label: 'Repeater',
    icon: 'repeat',
    hasSubTypes: false,
    allowedNestedTypes: ['Select', 'Text', 'Date'],
    configFields: ['nested_fields'],
  },
};

// Human-readable labels for select sub-types
export const SELECT_SUB_TYPE_LABELS = {
  Select: 'Dropdown',
  MultiSelect: 'Multi-Select',
  Autocomplete: 'Autocomplete',
  RadioButton: 'Radio',
  Checkbox: 'Checkbox',
  ChipAdd: 'Chips',
};

// Resolve Select sub-type based on allow_multiple and allow_custom_input.
// Filters out sub-types that are not applicable per the AC:
//   Select (Dropdown)   – Not available if allow_multiple OR allow_custom_input
//   MultiSelect          – Not available if !allow_multiple OR allow_custom_input
//   Autocomplete         – Always available
//   RadioButton (Radio)  – Not available if allow_multiple OR allow_custom_input
//   Checkbox             – Not available if !allow_multiple OR allow_custom_input
//   ChipAdd (Chips)      – Always available
export const resolveSelectSubType = (allowMultiple, allowCustomInput) => {
  const types = [];
  if (!allowMultiple && !allowCustomInput) types.push('Select');
  if (allowMultiple && !allowCustomInput) types.push('MultiSelect');
  types.push('Autocomplete');
  if (!allowMultiple && !allowCustomInput) types.push('RadioButton');
  if (allowMultiple && !allowCustomInput) types.push('Checkbox');
  types.push('ChipAdd');
  return types;
};

// Default built-in fields that every task type starts with
export const DEFAULT_FIELDS = [
  { id: 'f_title', field_type: 'TextField', label: 'Title', type_label: 'TEXT', is_required: true, is_enabled: true, locked: true, order: 0 },
  { id: 'f_description', field_type: 'RichText', label: 'Description', type_label: 'LONG TEXT', is_required: false, is_enabled: true, order: 1 },
  { id: 'f_assignee', field_type: 'UserSelect', label: 'Assignee', type_label: 'USER SELECT', is_required: true, is_enabled: true, order: 2 },
  { id: 'f_media', field_type: 'DropArea', label: 'Image & Media', type_label: 'MEDIA', is_required: false, is_enabled: true, order: 3 },
  { id: 'f_reference', field_type: 'TextField', label: 'Reference', type_label: 'REF', is_required: false, is_enabled: true, order: 4 },
  { id: 'f_due_date', field_type: 'KeyboardDatePicker', label: 'Due Date', type_label: 'DATE', is_required: false, is_enabled: true, order: 5 },
  { id: 'f_trade', field_type: 'TradeSelect', label: 'Trade Types', type_label: 'TRADE', is_required: false, is_enabled: true, order: 6 },
];

// Title field is always first and cannot be disabled or made optional
export const TITLE_FIELD = DEFAULT_FIELDS[0];

export const DEFAULT_CLOSEOUT = {
  notes_required: false,
  photos_required: false,
  signature_required: false,
};

export const CLOSEOUT_OPTIONS = [
  { label: 'Notes Required', value: 'notes_required' },
  { label: 'Photo Required', value: 'photos_required' },
  { label: 'Signature Required', value: 'signature_required' },
];

export const LOCATION_TYPE_OPTIONS = [
  { title: 'Division', value: 'division' },
  { title: 'Community', value: 'community' },
  { title: 'Lot', value: 'lot' },
];

export const MULTI_SELECT_BEHAVIOR_OPTIONS = [
  { title: 'Create One-by-one', value: 'one_by_one' },
  { title: 'Create All at Once', value: 'all_at_once' },
];

export const NESTED_FIELD_TYPES = [
  { title: 'Text', value: 'Text' },
  { title: 'Select', value: 'Select' },
  { title: 'Date', value: 'Date' },
  { title: 'Toggle', value: 'Toggle' },
  { title: 'Media', value: 'Media' },
];

export const SELECT_SUB_TYPE_OPTIONS = [
  { title: 'Dropdown', value: 'Select' },
  { title: 'Radio Buttons', value: 'RadioButton' },
];

export const EMAIL_BEHAVIOR_OPTIONS = [
  { label: 'Always Send an Email on Status Change', value: 'always_send', helperText: "Users can still change the email's recipients" },
  { label: 'Send Email by Default (Allow turn off)', value: 'send_by_default' },
  { label: 'Do Not Send Email by Default (Allow turn on)', value: 'do_not_send_by_default' },
  { label: 'Never Send an Email on Status Change', value: 'never_send', helperText: 'Users never even see the option to send emails' },
];

export const DEFAULT_EMAIL_BEHAVIOR = 'send_by_default';

export const MODES = {
  quality: { label: 'Quality', default: true },
  safety: { label: 'Safety', default: false },
};

// Get a category definition by its component type
export const getCategoryByComponent = (componentType) => {
  return Object.entries(FIELD_CATEGORIES).find(
    ([, v]) => v.component === componentType || v.subTypes?.includes(componentType)
  );
};

// Default config for each field category
export const getDefaultFieldConfig = (categoryKey) => {
  switch (categoryKey) {
    case 'Toggle':
      return { options: [{ title: 'YES', value: 'yes' }, { title: 'NO', value: 'no' }] };
    case 'Select':
      return { sub_type: 'Select', allow_multiple: false, allow_custom_input: false, options: [] };
    case 'Text':
      return { sub_type: 'TextField', prompt_text: '', default_text: '' };
    case 'Date':
      return { use_range: false, allow_previous_dates: true, default_date: null };
    case 'Media':
      return { sub_type: 'DropArea', file_types_allowed: ['image/*'], max_allowed: 10, allow_inserts: false };
    case 'Repeater':
      return { helper_text: '', item_name: '', nested_fields: [] };
    default:
      return {};
  }
};
